import { ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import { FormGroup, FormBuilder, UntypedFormBuilder, Validators } from '@angular/forms';
import { WINDOW } from '@benefitsui/app/lib/tokens/window';
import { RadioSelectData, ErrorControl, createErrorControlMap } from '@dxo/ng-swift-ui';
import { Constants } from '../../billing-page/billing-page.types';
import { BehaviorSubject } from 'rxjs';
import { DropdownOption } from '@benefitsui/app/lib/common.types';
import { claimTypeData, stateList, timeAwayData } from './create.types';

@Component({
  selector: 'app-create-claim',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss'],
  standalone: false
})
export class CreateClaimComponent implements OnInit {

  //field lengths
  NAME_MIN_LENGTH = 1;
  NAME_MAX_LENGTH = 40;
  STREET_ADDRESS_MAX_LENGTH = 100;
  LOCATION_MAX_LENGTH = 30;
  ZIP_CODE_LENGTH = 5;
  DATE_LENGTH = 8;
  SSN_LENGTH = 9;
  PHONE_NUMBER_LENGTH = 10;
  EXTENSION_MAX_LENGTH = 5;
  PROGRESS_BAR_INCREMENT = 3;

  
  //regex
  ssnPatternRegex = '^(?!666|000|9\\d{2})\\d{3}-(?!00)\\d{2}-(?!0{4})\\d{4}$';
  dateFormatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[1-2][0-9]|3[01])\/\d{4}$/;
  numericCharSet = /[0-9]/;
  numericCharSetWDash =/^[0-9]+(-[0-9]+)+$/;
  // eslint-disable-next-line
  dateCharRegex = /[0-9\-\/]/;
  
  //observables
  claimType$ = new BehaviorSubject<string>(Constants.StringEmpty);
  timeAwayReason$ = new BehaviorSubject<string>(Constants.StringEmpty);
  firstName$ = new BehaviorSubject<string>(Constants.StringEmpty);
  lastName$ = new BehaviorSubject<string>(Constants.StringEmpty);
  streetAddress$ = new BehaviorSubject<string>(Constants.StringEmpty);
  city$ = new BehaviorSubject<string>(Constants.StringEmpty);
  state$ = new BehaviorSubject<string>(Constants.StringEmpty);
  zipCode$ = new BehaviorSubject<string>(Constants.StringEmpty);
  birthdate$ = new BehaviorSubject<string>(Constants.StringEmpty);
  ssn$ = new BehaviorSubject<string>(Constants.StringEmpty);
  phoneNumber$ = new BehaviorSubject<string>(Constants.StringEmpty);
  
  //headers
  isStandard = true;
  isIllness = false;
  isPersonalized = false;
  illnessTitle = 'Create new illness claim';
  standardTitle = 'Create new claim';
  personalizedTitle = `Claim for Employee`;
  claimTypeCardHeader = 'What kind of claim are you filing?';
  timeAwayCardHeader = 'Why does your employee need time away from work?';
  nameCardHeader = 'Who is this illness claim for?';
  locationCardHeader = `Where does employee live?`;
  personalInfoCardHeader = 'Can you tell us more about employee?';
  
  //button disables
  isDisabledStep3 = true;
  isDisabledStep4 = true;
  isDisabledStep5 = true;
  progressPercentage = 0;
  
  activeStepIndex = 0;
  createClaimForm: FormGroup;

  //form labels 

  firstName = 'First name';
  lastName = 'Last name';
  streetAddress = 'Street address';
  city = 'City';
  state = 'State or territory';
  zipcode = 'ZIP code';
  birthdate = 'Birth date';
  ssn = 'Social Security number';
  phoneNumber = 'Phone number';
  extension = 'Extension';

  constructor(
    @Inject(WINDOW) private window: Window,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private readonly _formBuilder: FormBuilder
  ) {

    //create form
    this.createClaimForm = this.fb.group({
      claimType: [Constants.StringEmpty],
      timeAwayReason: [Constants.StringEmpty],
      firstName: [
        Constants.StringEmpty,
        [Validators.required, Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.NAME_MAX_LENGTH)]
      ],
      middleName: [
        Constants.StringEmpty,
        [Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.NAME_MAX_LENGTH)]
      ],
      lastName: [
        Constants.StringEmpty,
        [Validators.required, Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.NAME_MAX_LENGTH)]
      ],
      streetAddress: [
        Constants.StringEmpty,
        [
          Validators.required,
          Validators.minLength(this.NAME_MIN_LENGTH),
          Validators.maxLength(this.STREET_ADDRESS_MAX_LENGTH)
        ]
      ],
      city: [
        Constants.StringEmpty,
        [
          Validators.required,
          Validators.minLength(this.NAME_MIN_LENGTH),
          Validators.maxLength(this.LOCATION_MAX_LENGTH)
        ]
      ],
      state: [
        Constants.StringEmpty,
        [
          Validators.required,
          Validators.minLength(this.NAME_MIN_LENGTH),
          Validators.maxLength(this.LOCATION_MAX_LENGTH)
        ]
      ],
      zipCode: [
        Constants.StringEmpty,
        [Validators.required, Validators.minLength(this.ZIP_CODE_LENGTH), Validators.maxLength(this.ZIP_CODE_LENGTH)]
      ],
      birthdate: [
        Constants.StringEmpty,
        [Validators.required, Validators.pattern(this.dateFormatRegex), Validators.minLength(this.DATE_LENGTH + 2)]
      ],
      ssn: [
        Constants.StringEmpty,
        [Validators.required, Validators.pattern(this.ssnPatternRegex), Validators.minLength(this.SSN_LENGTH +2)]
      ],
      phoneNumber: [
        Constants.StringEmpty,
        [
          Validators.required,
          Validators.minLength(this.PHONE_NUMBER_LENGTH + 3)
        ]
      ],
      extension: [Constants.StringEmpty, [Validators.maxLength(this.EXTENSION_MAX_LENGTH)]]
    });
    this.formErrors = {
      ...this.getErrorMap(this.createClaimForm)
    };
  }

  //form step validation functions
  validateFormStep(step: number) {
    switch (step) {
      case 1:
        if (this.createClaimForm.controls.claimType.value !== '') {
          this.goToStep('next');
        }
        return;
      case 2:
        if (this.createClaimForm.controls.claimType.value !== '') {
          this.goToStep('next');
          this.isStandard = false;
          this.isIllness = true;
        }
        return;
      case 3:
        if (this.validateNameEntry()) {
          this.isDisabledStep3 = false;
        } else {
          this.isDisabledStep3 = true;
        }
        return;
      case 4:
        if (this.validAddressEntry()) {
          this.isDisabledStep4 = false;
        } else {
          this.isDisabledStep4 = true;
        }
        return;
      case 5:
        if (this.validatePersonalInfoEntry()) {
          this.isDisabledStep5 = false;
        } else {
          this.isDisabledStep5 = true;
        }
    }
  }

  validateNameEntry() {
    const firstName = this.formErrors.firstName.control.valid;
    const lastName = this.formErrors.lastName.control.valid;
    return firstName && lastName;
  }

  validAddressEntry() {
    const streetAddress = this.formErrors.streetAddress.control.valid;
    const city = this.formErrors.city.control.valid;
    const state = this.formErrors.state.control.valid;
    const zipCode = this.formErrors.zipCode.control.valid;
    return streetAddress && city && state && zipCode;
  }

  validatePersonalInfoEntry() {
    const birthdate = this.formErrors.birthdate.control.valid;
    const ssn = this.formErrors.ssn.control.valid;
    const phoneNumber = this.formErrors.phoneNumber.control.valid;
    return birthdate && ssn && phoneNumber;
  }

  //onInit
  ngOnInit(): void {
    this.activeStepIndex = 1;

    if (this.createClaimForm) {
      const claimTypeControl = this.createClaimForm.get('claimType');
      claimTypeControl?.valueChanges.subscribe((value) => {
        this.claimType$.next(value);
        this.validateFormStep(1);
      });

      const timeAwayReasonControl = this.createClaimForm.get('timeAwayReason');
      timeAwayReasonControl?.valueChanges.subscribe((value) => {
        this.timeAwayReason$.next(value);
        this.validateFormStep(2);
      });

      const firstNameControl = this.createClaimForm.get('firstName');
      firstNameControl?.valueChanges.subscribe((value) => {
        this.firstName$.next(value);
        this.validateFormStep(3);
      });

      const lastNameControl = this.createClaimForm.get('lastName');
      lastNameControl?.valueChanges.subscribe((value) => {
        this.lastName$.next(value);
        this.validateFormStep(3);
      });

      const streetAddressControl = this.createClaimForm.get('streetAddress');
      streetAddressControl?.valueChanges.subscribe((value) => {
        this.streetAddress$.next(value);
        this.validateFormStep(4);
      });

      const cityControl = this.createClaimForm.get('city');
      cityControl?.valueChanges.subscribe((value) => {
        this.city$.next(value);
        this.validateFormStep(4);
      });

      const stateControl = this.createClaimForm.get('state');
      stateControl?.valueChanges.subscribe((value) => {
        this.state$.next(value);
        this.validateFormStep(4);
      });

      const zipCodeControl = this.createClaimForm.get('zipCode');
      zipCodeControl?.valueChanges.subscribe((value) => {
        this.zipCode$.next(value);
        this.validateFormStep(4);
      });

      const phoneNumberControl = this.createClaimForm.get('phoneNumber');
      phoneNumberControl?.valueChanges.subscribe((value) => {
        this.phoneNumber$.next(value);
        this.validateFormStep(5);
      });

      const birthDateControl = this.createClaimForm.get('birthdate');
      birthDateControl?.valueChanges.subscribe((value) => {
        this.birthdate$.next(value);
        this.validateFormStep(5);
      });

      const ssnControl = this.createClaimForm.get('ssn');
      ssnControl?.valueChanges.subscribe((value) => {
        this.ssn$.next(value);
        this.validateFormStep(5);
      });
    }

    this.createClaimForm.controls.birthdate.valueChanges.subscribe((v) => 
      this.createClaimForm.controls.birthdate.setValue(this.formatDateWhenTyping(v), {emitEvent: false})
    
    )
  }

  goBackStep(step: number) {
    switch (step) {
      case 2:
        this.goToStep('prev');
        return;
      case 3:
        this.isIllness = false;
        this.isStandard = true;
        this.goToStep('prev');
        return;
      case 4:
        this.isPersonalized = false;
        this.isIllness = true;
        this.goToStep('prev');
        return;
    }
  }

  //form error handling
  formErrors: { [key: string]: ErrorControl };

  getErrorMap = (formGroup: FormGroup) =>
    createErrorControlMap(formGroup, {
      firstName: {
        required: this.createRequiredErrorMessage(this.firstName),
        maxlength: this.createMaxLengthErrorMessage(this.firstName, this.NAME_MAX_LENGTH),
        minlength: this.createMinLengthErrorMessage(this.firstName, this.NAME_MIN_LENGTH)
      },
      lastName: {
        required: this.createRequiredErrorMessage(this.lastName),
        maxlength: this.createMaxLengthErrorMessage(this.lastName, this.NAME_MAX_LENGTH),
        minlength: this.createMinLengthErrorMessage(this.lastName, this.NAME_MIN_LENGTH)
      },
      streetAddress: {
        required: this.createRequiredErrorMessage(this.streetAddress),
        maxlength: this.createMaxLengthErrorMessage(this.streetAddress, this.STREET_ADDRESS_MAX_LENGTH),
        minlength: this.createMinLengthErrorMessage(this.streetAddress, this.NAME_MIN_LENGTH)
      },
      city: {
        required: this.createRequiredErrorMessage(this.city),
        maxlength: this.createMaxLengthErrorMessage(this.city, this.LOCATION_MAX_LENGTH),
        minlength: this.createMinLengthErrorMessage(this.city, this.NAME_MIN_LENGTH)
      },
      state: {
        required: this.createRequiredErrorMessage(this.state),
        maxlength: this.createMaxLengthErrorMessage(this.state, this.LOCATION_MAX_LENGTH),
        minlength: this.createMinLengthErrorMessage(this.state, this.NAME_MIN_LENGTH)
      },
      zipCode: {
        required: this.createRequiredErrorMessage(this.zipcode),
        minlength: this.createExactLengthErrorMessage(this.zipcode, this.ZIP_CODE_LENGTH)
      },
      birthdate: {
        required: this.createRequiredErrorMessage(this.birthdate),
        minlength: this.createExactLengthErrorMessage(this.birthdate, this.DATE_LENGTH)
      },
      ssn: {
        required: this.createRequiredErrorMessage(this.ssn),
        minlength: this.createExactLengthErrorMessage(this.ssn, this.SSN_LENGTH)
      },
      phoneNumber: {
        required: this.createRequiredErrorMessage(this.phoneNumber),
        minlength: this.createExactLengthErrorMessage(this.phoneNumber, this.PHONE_NUMBER_LENGTH)
      }
    }) as object as {
      firstName: ErrorControl;
      lastName: ErrorControl;
      streetAddress: ErrorControl;
      city: ErrorControl;
      state: ErrorControl;
      zipCode: ErrorControl;
      birthdate: ErrorControl;
      ssn: ErrorControl;
      phoneNumber: ErrorControl;
    };

    createRequiredErrorMessage (fieldName: string){
      if(fieldName === this.ssn || fieldName === this.zipcode){
        return `Enter ${fieldName}`;
      }else{
        return `Enter ${fieldName.toLowerCase()}`
      }
    }

    createMinLengthErrorMessage (fieldName: string, characterCount: number){
      return `${fieldName} must be at least ${characterCount} characters`
    }

    createExactLengthErrorMessage (fieldName: string, characterCount: number){
      return `${fieldName} must be ${characterCount} digits`
    }

    createMaxLengthErrorMessage (fieldName: string, characterCount: number) {
      return `${fieldName} must not exceed ${characterCount} characters`
    }

  //back and next button control
  goToStep(step: string): void {
    this.activeStepIndex = step === 'prev' ? this.activeStepIndex - 1 : this.activeStepIndex + 1;

    if (this.progressPercentage > 0 && step === 'prev') {
      this.progressPercentage -= this.PROGRESS_BAR_INCREMENT;
    }
    if (this.activeStepIndex > 2 && this.progressPercentage < 100 && step === 'next') {
      this.progressPercentage += this.PROGRESS_BAR_INCREMENT;
    }

    this.pageHeaderChange(step);
  }

  //header control
  pageHeaderChange(step: string) {
    if (this.activeStepIndex === 4 && step === 'next') {
      const firstName = this.createClaimForm.controls.firstName.value;
      const lastName = this.createClaimForm.controls.lastName.value;
      this.locationCardHeader = `Where does ${firstName} live? `;
      this.personalizedTitle = `Claim for ${firstName} ${lastName}`;
      this.personalInfoCardHeader = `Can you tell us more about ${firstName}?`;
      this.isIllness = false;
      this.isPersonalized = true;
    }
  }

  //formatting functions 

  formatSSN(value: string): string {
    const v = value.replace(/\D/g, Constants.StringEmpty).slice(0, 9);
    const arr = v.match(/(\d{1,3})(\d{1,2})?(\d{1,4})?/);
    if (arr) {
      return `${arr[1] || Constants.StringEmpty}${arr[2] ? '-' + arr[2] : Constants.StringEmpty}${arr[3] ? '-' + arr[3] : Constants.StringEmpty}`;
    }
    return Constants.StringEmpty;
  }

  formatPhoneNumber(value: string): string {
    const v = value.replace(/\D/g, Constants.StringEmpty).slice(0, 12);
    const arr = v.match(/(\d{1,3})?(\d{1,3})?(\d{1,4})/);
    if (arr) {
      const areaCode = `${arr[1] ? '(' + arr[1] + ')' : Constants.StringEmpty}`;
      const firstThree = `${arr[2] ? arr[2] : Constants.StringEmpty}`;
      const lastFour = `${arr[3] ? '-' + arr[3] : Constants.StringEmpty}`;

      return `${areaCode}${firstThree}${lastFour}`;
    }
    return Constants.StringEmpty;
  }

  onPhoneNumberInputChange(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const formattedPhoneNumber = this.formatPhoneNumber(inputValue);
    this.createClaimForm.controls['phoneNumber'].setValue(formattedPhoneNumber, { emitEvent: false });
  }

  onSSNInputChange(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const formattedSSN = this.formatSSN(inputValue);
    this.createClaimForm.controls['ssn'].setValue(formattedSSN, { emitEvent: false });
  }

    formatDateWhenTyping(value: string | null) {
    if (value === null) {
      return Constants.StringEmpty;
    }
    value = value.split('-').join('/');
    const parts = value.split('/');
    for (let i = 0; i < parts.length - 1; i++) {
      parts[i] = parts[i].padStart(2, '0');
    }

    value = parts.join('/');
    if (value.length > 2 && !isNaN(parseInt(value[2], 10))) {
      value = value.slice(0, 2) + '/' + value[2];
    }
    if (value.length > 5 && !isNaN(parseInt(value[5], 10))) {
      value = value.slice(0, 5) + '/' + value[5];
    }
    return value;
  }

  //multiselect options

  statesDropdown: DropdownOption<string>[] = stateList;

  claimTypeRadioData: RadioSelectData[] = claimTypeData;

  timeAwayRadioData: RadioSelectData[] = timeAwayData;
}
